import React from 'react';
import classNames from 'classnames';
import { useFormContext, useController } from 'react-hook-form';
import { IoInformationCircleSharp, IoCloseCircleSharp } from 'react-icons/io5';
import styled from 'styled-components';
import { isNaN } from 'lodash';
import numeral from 'numeral';

import { ErrorMessage, OpenCloseAnimation } from 'playup-web-core';

import { getSignupConfig } from '../../../api/users';
import { LimitTypes } from '../../../Account/constants/limits';

const SignUpDepositLimits = () => {
	const [showTooltip, setShowInfo] = React.useState(false);
	const [depositLimitsConfig, setDepositLimitsConfig] = React.useState<PortalLimit>();

	const { setValue, watch } = useFormContext<SignupFormData>();
	const hasSetDepositLimit = watch('set_deposit_limit');

	const { field: setDepositLimitField, fieldState: setDepositLimitFieldState } = useController({
		name: 'set_deposit_limit',
	});

	const { field: limitAmountField, fieldState: limitAmountFieldState } = useController({
		name: 'deposit_limit.limit',
	});

	const { field: durationTypeIdField, fieldState: durationTypeIdFieldState } = useController({
		name: 'deposit_limit.duration_type_id',
	});
	// get signup configuration (currently is just for deposit limits)
	React.useEffect(() => {
		getSignupConfig().then(({ data }) => {
			if (!data?.available_limits) return;
			const depositLimits = data.available_limits.find((limit) => limit.id === LimitTypes.Deposit);
			if (depositLimits) {
				setDepositLimitsConfig(depositLimits);
			}
		});
	}, []);

	React.useEffect(() => {
		if (depositLimitsConfig?.durations[1]?.id) {
			setValue('deposit_limit.duration_type_id', depositLimitsConfig?.durations[1].id + '');
		}
	}, [depositLimitsConfig, setValue]);

	React.useEffect(() => {
		if (!showTooltip) return;
		const listener = () => {
			setShowInfo(false);
		};
		document.body.addEventListener('click', listener);

		return () => {
			document.body.removeEventListener('click', listener);
		};
	}, [showTooltip]);

	const depositLimitHandler = (hasDepositLimit: boolean) => () => {
		setValue('set_deposit_limit', hasDepositLimit, { shouldValidate: true });
	};

	const setDepositAmount = (e) => {
		if (e.target.value === '') return;
		let numericValue = parseFloat(e.target.value);
		if (isNaN(numericValue)) numericValue = 0;

		const formattedValue = numeral(numericValue).format('0.[00]');
		if (formattedValue !== e.target.value) setValue('deposit_limit.limit', parseFloat(formattedValue));
	};

	return (
		<Wrapper>
			<label>
				Deposit Limit{' '}
				<div className="dialog-wrapper">
					{!showTooltip ? (
						<IoInformationCircleSharp
							color="#707070"
							onClick={() => {
								setShowInfo(true);
							}}
						/>
					) : (
						<IoCloseCircleSharp color="#707070" />
					)}
					<dialog className={showTooltip ? 'visible' : undefined}>
						Enabling Deposit Limit allows you to place a limit on the amount you can deposit for a specific period of
						time. If you lower your limit it will also take effect immediately. If you increase your limit, the change
						will only take effect 7 days after it has been received.
					</dialog>
				</div>
			</label>

			<div className="row-buttons">
				<div className="button-wrapper">
					<button
						onClick={depositLimitHandler(false)}
						type="button"
						className={classNames({
							selected: hasSetDepositLimit === false,
							error: setDepositLimitFieldState.error,
						})}
					>
						No Deposit Limit
					</button>
				</div>
				<div className="button-wrapper">
					<button
						onClick={depositLimitHandler(true)}
						type="button"
						className={classNames({
							'selected open': hasSetDepositLimit,
							error: setDepositLimitFieldState.error,
						})}
					>
						Set a Deposit Limit
					</button>
				</div>
			</div>
			<input
				type="hidden"
				ref={setDepositLimitField.ref}
				onChange={setDepositLimitField.onChange}
				value={setDepositLimitField.value}
			/>

			<div className={classNames({ 'inputs-accordion': true, active: !!hasSetDepositLimit })}>
				<OpenCloseAnimation isOpen={!!hasSetDepositLimit} duration={0.2}>
					<div className="row-spacer"></div>
					<div className="row-inputs">
						<div className="input-group">
							<label>Amount</label>
							<StyledWrapper className="input-text" hasError={limitAmountFieldState.error}>
								<span>$</span>
								<input
									ref={limitAmountField.ref}
									value={limitAmountField.value}
									onChange={limitAmountField.onChange}
									placeholder="0.00"
									type="number"
									step={0.01}
									onBlur={setDepositAmount}
								/>
							</StyledWrapper>
						</div>
						<div className="input-group">
							<label>Period</label>
							<select
								ref={durationTypeIdField.ref}
								value={durationTypeIdField.value}
								onChange={durationTypeIdField.onChange}
								className={classNames(durationTypeIdFieldState.error && 'validation-error')}
							>
								{depositLimitsConfig?.durations.map((duration) => (
									<option key={duration.id} value={duration.id}>
										{duration.name}
									</option>
								))}
							</select>
						</div>
					</div>
				</OpenCloseAnimation>
			</div>
			{setDepositLimitFieldState.error && (
				<ErrorMessage textAlign="left">{setDepositLimitFieldState.error?.message}</ErrorMessage>
			)}
			{setDepositLimitField.value && limitAmountFieldState.error && (
				<ErrorMessage textAlign="left">{limitAmountFieldState.error?.message}</ErrorMessage>
			)}
			{durationTypeIdFieldState.error && (
				<ErrorMessage textAlign="left">{durationTypeIdFieldState.error?.message}</ErrorMessage>
			)}
		</Wrapper>
	);
};

export default SignUpDepositLimits;

//TODO: better to replace boolean set_deposit_limit with this so we can have a default value to avoid warning message
const DepositLimitStatus = {
	NotSet: 'NotSet',
	Yes: 'Yes',
	No: 'No',
};

//TODO: This is really bad. Need to break down.
const Wrapper = styled.div`
	--button-internal-margin: 0.25rem;
	color: ${({ theme }) => theme.colors.darkR};

	label {
		display: flex;
		align-content: center;
		margin-bottom: 0.3rem;

		svg {
			margin-left: 0.2rem;
			position: relative;
			z-index: 1;
			cursor: pointer;
			transition: opacity 0.3s;

			&:hover {
				opacity: 0.7;
			}
		}

		.dialog-wrapper {
			position: relative;

			dialog {
				display: none;
				border-radius: 0.5rem;
				background-color: #efefef;
				position: absolute;
				width: 300px;
				padding: 1rem 1rem 1rem 1.5rem;
				bottom: -3px;
				left: -5px;
				font-size: 0.8rem;

				&.visible {
					display: block;
				}
			}
		}
	}

	.row-buttons {
		display: flex;

		.button-wrapper {
			flex-basis: 50%;

			&:nth-child(1) {
				padding-right: var(--button-internal-margin);
			}
			&:nth-child(2) {
				padding-left: var(--button-internal-margin);
			}

			button {
				width: 100%;
				padding: 1rem;
				border-radius: 0.5rem;
				background-color: #d0d0d0;
				opacity: 0.4;
				outline: none;
				transition: opacity 0.3s;
				border: 1px solid #d0d0d0;

				&:hover {
					opacity: 0.8;
				}

				&.error {
					border-color: ${({ theme }) => theme.colors.danger};
				}

				&.selected {
					opacity: 1;
					&.open {
						border-bottom-left-radius: 0;
						border-bottom-right-radius: 0;
					}
				}
			}
		}
	}

	.inputs-accordion {
		opacity: 0.4;

		&.active {
			opacity: 1;
		}

		.row-spacer {
			display: flex;
			justify-content: flex-end;
			padding-left: calc(var(--button-internal-margin) * 2);

			&:after {
				content: '';
				height: calc(var(--button-internal-margin) * 2);
				flex-basis: 50%;
				background-color: #d0d0d0;
			}
		}

		.row-inputs {
			display: flex;
			background-color: #d0d0d0;
			border-radius: 0.5rem 0 0.5rem 0.5rem;
			padding: 1rem;

			.input-group {
				flex: 1;
				label {
					display: block;
				}

				&:nth-child(1) {
					margin-right: 0.25rem;
				}
				&:nth-child(2) {
					margin-left: 0.25rem;
				}

				select {
					width: 100%;
					background-color: white;
					padding: 0.25rem 0.5rem;
					height: 2rem;
					border-radius: 0.25rem;
					border: 1px solid transparent;

					&:invalid,
					&.validation-error {
						border-width: 2px
						border-color: ${({ theme }) => theme.colors.danger};
					}
				}

				.input-text {
					display: flex;
					align-items: center;

					input {
						flex-grow: 1;
					}

					span {
						margin-right: 0.25rem;
						font-size: 0.9rem;
					}
				}
			}
		}
	}
`;

const StyledWrapper = styled.div<{ hasError: boolean }>(({ theme, hasError }) => ({
	width: '100%',
	borderWidth: hasError ? '2px' : '1px',
	borderStyle: 'solid',
	borderColor: hasError ? theme.colors.danger : 'transparent',
	borderRadius: '0.25rem',
	backgroundColor: theme.colors.white,
	padding: '0.25rem 0.5rem',
	height: '2rem',
}));
